import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ComoConfirmUsePointModalConfig as ComoConfirmUsePointModalContentConfig } from '@front-platform/content-builder';
import {
  comoConfirmUsePointModalResolveConfig,
  resolveCancelButtonColors,
  resolveValidateButtonColors,
} from '@front-platform/content-builder';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Component, input, computed } from '@angular/core';

import { ComoAsset } from '@core/models/como.model';
import { ModalsService } from '@core/services/modals/modals.service';
import { ComoService } from 'src/app/core/services/como/como.service';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { GetModalInterface, ModalConfig } from '@core/services/modals/modals-util.type';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { TranslationField } from '@shared/interfaces/translation-field.interface';

export type ComoConfirmUsePointModalConfig = ModalConfig<{
  handle: typeof ComoConfirmUsePointModalComponent.handle;
  inputs: {
    title?: TranslationField;
    cancelText?: TranslationField;
    validateText?: TranslationField;
    cancelColor?: ContentBuilderFieldColors;
    validateColor?: ContentBuilderFieldColors;
  };
}>;
export interface ComoConfirmUsePointModalRuntimeConfig {
  'asset'?: ComoAsset;
}

export type ComoConfirmUsePointModalConfigInput = Partial<
  Omit<ComoConfirmUsePointModalContentConfig, keyof ComoConfirmUsePointModalRuntimeConfig> &
    ComoConfirmUsePointModalRuntimeConfig
>;
export type ComoConfirmUsePointModalResolvedConfig = Omit<
  ComoConfirmUsePointModalContentConfig,
  keyof ComoConfirmUsePointModalRuntimeConfig
> &
  Partial<ComoConfirmUsePointModalRuntimeConfig>;
export interface ComoConfirmUsePointModalContract extends ConfigComponentContract<
  ComoConfirmUsePointModalConfigInput,
  ComoConfirmUsePointModalResolvedConfig
> {}

@Component({
  selector: 'app-como-confirm-use-point-modal',
  templateUrl: './como-confirm-use-point-modal.component.html',
  standalone: false,
})
export class ComoConfirmUsePointModalComponent
  implements GetModalInterface<ComoConfirmUsePointModalConfig>, ComoConfirmUsePointModalContract
{
  readonly config = input<ComoConfirmUsePointModalConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    ComoConfirmUsePointModalContentConfig,
    ComoConfirmUsePointModalRuntimeConfig
  >(comoConfirmUsePointModalResolveConfig, () => ({ config: this.config() }));
  static handle = 'como-confirm-use-point-modal' as const;
  get asset(): ComoAsset {
    return this.resolvedConfig()['asset'] as unknown as ComoAsset;
  }

  readonly title = computed(() => this.resolvedConfig()['title'] as unknown as TranslationField);
  readonly backgroundColor = computed(
    () => this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly cancelText = computed(
    () => this.resolvedConfig()['cancel_text'] as unknown as TranslationField,
  );
  readonly validateText = computed(
    () => this.resolvedConfig()['validate_text'] as unknown as TranslationField,
  );
  readonly cancelColor = computed(() => {
    const cfg = this.resolvedConfig() as Record<string, unknown>;
    return resolveCancelButtonColors(cfg, this.config() as Record<string, unknown>);
  });
  readonly validateColor = computed(() => {
    const cfg = this.resolvedConfig() as Record<string, unknown>;
    return resolveValidateButtonColors(cfg, this.config() as Record<string, unknown>);
  });

  index$: Observable<number> = this.modalSelector
    .selectModal(ComoConfirmUsePointModalComponent.handle)
    .pipe(map(el => (el?.index ? el.index : 4)));

  isProcessing = false;
  keyboardOffset = 0; // For keyboard-aware positioning

  constructor(
    private modalSelector: ModalStoreSelector,
    private readonly comoService: ComoService,
    private readonly modalsService: ModalsService,
  ) {}

  getAssetName(): string | undefined {
    return this.asset?.name;
  }

  confirm(): void {
    this.close();
    this.comoService.displayProductsSelectionModal(this.asset);
  }

  close(): void {
    this.modalsService.close(ComoConfirmUsePointModalComponent.handle);
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ComoConfirmUsePointModalComponent, {
  'keyAliases': {},
});
